/**
 * Snapshot of the controller's xDS server: connected Envoy nodes,
 * their per-type ACK status, and the controller-side push latency.
 *
 * This is the operator-facing view of the consistency-guarantee
 * machinery added in `controller/controller/xds/snapshot.py` —
 * `wait_for_ack()` is what blocks the drain orchestrator from
 * tearing down a pod before every Envoy has acknowledged the new
 * EDS version. Surfacing it here lets operators spot stuck nodes
 * without grepping the controller logs.
 */
import { Activity, Cable } from "lucide-react";

import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";

/**
 * Mirrors `controller.api.routers.xds.NodeStatusOut` (the read API
 * over `controller/controller/xds/snapshot.py` `NodeStatus`).
 */
export interface XdsNodeStatusOut {
  node_id: string;
  type_url: string;
  last_pushed_version: string | null;
  last_acked_version: string | null;
  last_ack_latency_seconds: number | null;
  nack_errors: string[];
  last_seen_at: string | null;
  last_pushed_at: string | null;
}

export interface XdsHealthCardProps {
  nodes: XdsNodeStatusOut[];
  /** Latest snapshot version for the type_url (read-only). */
  currentVersion?: string;
  loading?: boolean;
}

export function XdsHealthCard({
  nodes,
  currentVersion,
  loading,
}: XdsHealthCardProps) {
  return (
    <Section
      title="xDS health"
      icon={<Cable />}
      count={
        <Pill tone={nodes.length === 0 ? "warn" : "ok"}>
          {nodes.length} node{nodes.length === 1 ? "" : "s"}
        </Pill>
      }
      flush
    >
      {currentVersion && (
        <div
          className="muted"
          style={{ fontSize: 12, padding: "0 12px 8px" }}
        >
          current snapshot version{" "}
          <span className="mono">{currentVersion}</span>
        </div>
      )}
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Node</th>
              <th>Type</th>
              <th>Last pushed</th>
              <th>Last ACKed</th>
              <th style={{ textAlign: "right" }}>ACK lag</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {nodes.map((n) => {
              const inSync =
                n.last_pushed_version != null &&
                n.last_acked_version === n.last_pushed_version;
              const tone = n.nack_errors.length > 0
                ? "danger"
                : inSync
                  ? "ok"
                  : "warn";
              const label = n.nack_errors.length > 0
                ? "NACK"
                : inSync
                  ? "ACKed"
                  : "lagging";
              return (
                <tr key={`${n.node_id}/${n.type_url}`}>
                  <td className="mono">{n.node_id}</td>
                  <td className="mono muted" style={{ fontSize: 11 }}>
                    {shortType(n.type_url)}
                  </td>
                  <td className="mono">{n.last_pushed_version ?? "—"}</td>
                  <td className="mono">{n.last_acked_version ?? "—"}</td>
                  <td className="num mono">
                    {n.last_ack_latency_seconds != null
                      ? `${(n.last_ack_latency_seconds * 1000).toFixed(0)} ms`
                      : "—"}
                  </td>
                  <td>
                    <Pill tone={tone}>
                      <Activity />
                      {label}
                    </Pill>
                    {n.nack_errors.length > 0 && (
                      <span
                        className="muted mono"
                        style={{ display: "block", fontSize: 10, marginTop: 2 }}
                        title={n.nack_errors.join("\n")}
                      >
                        {n.nack_errors[0]}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!nodes.length && !loading && (
              <tr>
                <td colSpan={6} className="empty">
                  no Envoy nodes connected to the xDS server
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={6} className="empty muted">
                  loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function shortType(typeUrl: string): string {
  const slash = typeUrl.lastIndexOf("/");
  return slash >= 0 ? typeUrl.slice(slash + 1) : typeUrl;
}
